import React, { useState, useEffect } from "react"
import { Col, Row, UncontrolledAlert, Spinner } from "reactstrap"
import api from "API/Api"
import { AvForm, AvField, AvRadioGroup, AvRadio } from "availity-reactstrap-validation"
import { Link } from "react-router-dom"
import PageLoader from "./../../../assets/loading/pageLoad1.gif"

const LEVEL_DESCRIPTIONS = {
  "Admin": "Full system access — all modules, all permissions",
  "Company": "Company-wide management — plants, billing, finance, reports",
  "Plant": "Plant operations — chilling centers, routes, procurement, billing",
  "CC": "Chilling center operations — routes, purchases, basic billing",
  "Route": "Route management — collection centers, purchases",
  "Center": "Collection center — data entry and view only",
  "Client": "Read-only access — dashboard and reports only",
}

const LEVEL_COLORS = {
  "Admin": "#dc2626",
  "Company": "#6366f1",
  "Plant": "#0ea5e9",
  "CC": "#22c55e",
  "Route": "#f59e0b",
  "Center": "#8b5cf6",
  "Client": "#6c757d",
}

const AddRole = () => {
  const [submitted, setSubmitted] = useState(false)
  const [loading, setLoading] = useState(false)
  const [pageLoad, setPageLoad] = useState(true)
  const [roleAccess, setRoleAccess] = useState([])
  const [roleSets, setRoleSets] = useState([])
  const [roleFilters, setRoleFilters] = useState({})
  const [all, setAll] = useState(false)
  const [roleid, setRoleid] = useState("")
  const [selectedLevel, setSelectedLevel] = useState("")
  const [modulesLoading, setModulesLoading] = useState(false)

  useEffect(() => { loadInitialData() }, [])

  const loadInitialData = async () => {
    try {
      const access = await api.get("accesser")
      setRoleAccess(Array.isArray(access.data.data) ? access.data.data : access.data.data?.data || [])
    } catch (e) { console.error("Failed to load access levels:", e) }
    setPageLoad(false)
  }

  const loadModules = async (accessLevelId) => {
    setModulesLoading(true)
    try {
      const resp = await api.get("role-settings/" + accessLevelId)
      const moduleData = Array.isArray(resp.data.data) ? resp.data.data : resp.data.data?.data || []
      setRoleSets(moduleData)
      setRoleFilters(JSON.parse(resp.data.settings || "{}"))
    } catch (e) {
      console.error("Failed to load role settings:", e)
      setRoleSets([])
      setRoleFilters({})
    }
    setModulesLoading(false)
  }

  const handleValidSubmit = async (event, values) => {
    if (!roleid) { alert("Please select a Role Access Level first"); return }
    setLoading(true); setSubmitted(false)
    try {
      const payload = {
        ...values,
        access_level: selectedLevel,
        role_settings: JSON.stringify(roleFilters)
      }
      const resp = await api.post("create-role", payload)
      if (resp.data.success) { setSubmitted(true); setTimeout(() => setSubmitted(false), 4000) }
    } catch (e) { console.error(e) }
    setLoading(false)
  }

  const handleRadioChange = async (event, roleId, levelName) => {
    setRoleid(roleId)
    setSelectedLevel(levelName)
    setAll(false)
    await loadModules(roleId)
  }

  const handleCheckboxChange = (event, id, type) => {
    if (!event.target.checked) setAll(false)
    const selectedItems = (roleFilters[id] || "0000000").split("")
    selectedItems[type] = event.target.checked ? "1" : "0"
    setRoleFilters(prev => ({ ...prev, [id]: selectedItems.join("") }))
  }

  const handleParentCheckboxChange = async (event) => {
    const checked = event.target.checked
    setAll(checked)
    try {
      const resp = await api.get("role-options/" + roleid + "/" + (checked ? "checked" : "unchecked"))
      setRoleFilters(JSON.parse(resp.data.data))
    } catch (e) { console.error(e) }
  }

  // Group modules by category
  const groupedModules = roleSets.reduce((acc, mod) => {
    const cat = mod.category || "Other"
    if (!acc[cat]) acc[cat] = []
    acc[cat].push(mod)
    return acc
  }, {})

  // Count only AVAILABLE permissions per module (not all 7 bits)
  const PERM_KEYS = ["view", "add", "edit", "delete", "export", "approve", "statusupdate"]
  const getAvailableCount = (moduleId, filterStr) => {
    const mod = roleSets.find(m => m.id === moduleId)
    if (!mod) return { enabled: 0, total: 0 }
    const bits = (filterStr || "0000000").split("")
    let enabled = 0, total = 0
    PERM_KEYS.forEach((key, idx) => {
      if (mod[key] === 1) { total++; if (bits[idx] === "1") enabled++ }
    })
    return { enabled, total }
  }
  const permCount = Object.entries(roleFilters).reduce((sum, [id, v]) => sum + getAvailableCount(parseInt(id), v).enabled, 0)
  const totalSlots = Object.entries(roleFilters).reduce((sum, [id, v]) => sum + getAvailableCount(parseInt(id), v).total, 0)

  const CATEGORY_ICONS = {
    "Overview": "fa-tachometer-alt", "Masters": "fa-database", "Procurement": "fa-boxes",
    "Session": "fa-calendar-check", "Billing": "fa-file-invoice-dollar", "Finance": "fa-wallet",
    "System": "fa-cogs", "Reports": "fa-chart-bar", "Other": "fa-ellipsis-h"
  }
  const CATEGORY_COLORS = {
    "Overview": "#6366f1", "Masters": "#0ea5e9", "Procurement": "#22c55e",
    "Session": "#a855f7", "Billing": "#f59e0b", "Finance": "#ef4444",
    "System": "#8b5cf6", "Reports": "#06b6d4", "Other": "#6c757d"
  }

  if (pageLoad) return <div className="pageLoader"><img src={PageLoader} alt="Loading..." /></div>

  return (
    <div className="dashBody">
      <div className="font-size-18 mb-0 dashHeader">
        <Row>
          <Col md={6}><h4>Add Role</h4></Col>
          <Col md={6} style={{ textAlign: "right" }}><Link to="/roles" className="dashBtn"><i className="fas fa-chevron-left"></i> <b>Back</b></Link></Col>
        </Row>
      </div>
      <div className="dashContent">
        <AvForm className="form-horizontal mt-0" onValidSubmit={(e, v) => handleValidSubmit(e, v)}>
          {/* Role Name */}
          <h6 style={{ color: "#6366f1", borderBottom: "2px solid #e2e8f0", paddingBottom: "8px", marginBottom: "16px" }}>
            <i className="fas fa-user-shield"></i> Role Information
          </h6>
          <Row>
            <Col md={12}><div className="mb-3">
              <AvField name="setting_name" label="Role Name" placeholder="e.g. Plant Manager, Route Supervisor, Billing Clerk..." type="text" required />
            </div></Col>
          </Row>

          {/* Access Level Selection */}
          <h6 style={{ color: "#6366f1", borderBottom: "2px solid #e2e8f0", paddingBottom: "8px", marginBottom: "16px" }}>
            <i className="fas fa-layer-group"></i> Access Level
            <small style={{ color: "#6c757d", fontWeight: "400", marginLeft: "8px" }}>Choose the organizational level for this role</small>
          </h6>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "10px", marginBottom: "20px" }}>
            {roleAccess.map((ra, i) => {
              const isSelected = String(roleid) === String(ra.id)
              const color = LEVEL_COLORS[ra.access_level] || "#6c757d"
              return (
                <div key={i} onClick={() => handleRadioChange(null, ra.id, ra.access_level)}
                  style={{
                    border: isSelected ? `2px solid ${color}` : "2px solid #e2e8f0",
                    borderRadius: "10px", padding: "12px 20px", cursor: "pointer", minWidth: "120px",
                    background: isSelected ? `${color}10` : "#fff", transition: "all 0.2s ease",
                    textAlign: "center", position: "relative"
                  }}>
                  {isSelected && <i className="fas fa-check-circle" style={{ position: "absolute", top: "4px", right: "6px", color, fontSize: "14px" }}></i>}
                  <div style={{ fontWeight: "700", fontSize: "14px", color: isSelected ? color : "#1e293b" }}>{ra.access_level}</div>
                  <div style={{ fontSize: "10px", color: "#6c757d", marginTop: "2px" }}>{LEVEL_DESCRIPTIONS[ra.access_level] ? LEVEL_DESCRIPTIONS[ra.access_level].split("—")[0] : ""}</div>
                </div>
              )
            })}
          </div>
          {/* Hidden field for form validation */}
          <input type="hidden" name="access_level" value={roleid} />

          {/* Level description */}
          {selectedLevel && (
            <div style={{ background: `${LEVEL_COLORS[selectedLevel] || "#6c757d"}10`, border: `1px solid ${LEVEL_COLORS[selectedLevel] || "#6c757d"}30`,
              borderRadius: "8px", padding: "10px 16px", marginBottom: "16px", fontSize: "13px" }}>
              <i className="fas fa-info-circle" style={{ color: LEVEL_COLORS[selectedLevel], marginRight: "8px" }}></i>
              <strong>{selectedLevel}:</strong> {LEVEL_DESCRIPTIONS[selectedLevel] || ""}
              <span style={{ float: "right", fontSize: "12px", color: "#6c757d" }}>
                {roleSets.length} modules • {permCount} permissions pre-set
              </span>
            </div>
          )}

          {/* Permissions Section */}
          <div className="rolePermission" style={{ marginTop: "8px" }}>
            <div className="headeeRoles" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 style={{ margin: 0, fontSize: "16px" }}>
                <i className="fas fa-shield-alt" style={{ marginRight: "8px" }}></i>
                Role Permissions
                {modulesLoading && <Spinner size="sm" color="primary" className="ms-2" />}
              </h2>
              {roleSets.length > 0 && (
                <label style={{ cursor: "pointer", fontWeight: "600", fontSize: "13px", display: "flex", alignItems: "center", gap: "6px" }}>
                  <input type="checkbox" checked={all} onChange={handleParentCheckboxChange} style={{ width: "16px", height: "16px" }} />
                  Select All ({permCount}/{totalSlots})
                </label>
              )}
            </div>

            {/* Empty state */}
            {!roleid && !modulesLoading && (
              <div style={{ padding: "50px 20px", textAlign: "center", color: "#6c757d", background: "#f8f9fa", borderRadius: "8px", marginTop: "12px" }}>
                <i className="fas fa-hand-pointer" style={{ fontSize: "36px", marginBottom: "12px", color: "#cbd5e1" }}></i>
                <p style={{ margin: 0, fontSize: "15px", fontWeight: "500" }}>Select an Access Level above to configure permissions</p>
                <p style={{ margin: "4px 0 0", fontSize: "12px" }}>Each level comes with intelligent default permissions based on the dairy hierarchy</p>
              </div>
            )}

            {/* Module groups */}
            {roleid && Object.entries(groupedModules).map(([category, modules]) => (
              <div key={category} style={{ marginTop: "16px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px", padding: "6px 12px", background: `${CATEGORY_COLORS[category] || "#6c757d"}10`, borderRadius: "6px", borderLeft: `3px solid ${CATEGORY_COLORS[category] || "#6c757d"}` }}>
                  <i className={`fas ${CATEGORY_ICONS[category] || "fa-folder"}`} style={{ color: CATEGORY_COLORS[category] || "#6c757d", fontSize: "13px" }}></i>
                  <strong style={{ fontSize: "13px", color: CATEGORY_COLORS[category] || "#6c757d", textTransform: "uppercase", letterSpacing: "0.5px" }}>{category}</strong>
                  <span style={{ fontSize: "11px", color: "#6c757d" }}>({modules.length})</span>
                </div>
                {modules.map((rs, i) => {
                  const perms = (roleFilters[rs.id] || "0000000").split("")
                  const activeCount = perms.filter(c => c === "1").length
                  return (
                    <Row key={rs.id} style={{ padding: "7px 12px", borderBottom: "1px solid #f1f3f5", alignItems: "center",
                      background: activeCount > 0 ? "#fafffe" : "#fff" }}>
                      <Col md={4}>
                        <span style={{ fontSize: "14px", fontWeight: activeCount > 0 ? "600" : "400", color: activeCount > 0 ? "#1e293b" : "#94a3b8" }}>
                          {rs.setting_name}
                        </span>
                        {activeCount > 0 && <span className="badge bg-success ms-2" style={{ fontSize: "9px" }}>{activeCount}</span>}
                      </Col>
                      <Col md={8}>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                          {[
                            { key: "view", label: "View", idx: 0, icon: "fa-eye", color: "#0ea5e9" },
                            { key: "add", label: "Add", idx: 1, icon: "fa-plus", color: "#22c55e" },
                            { key: "edit", label: "Edit", idx: 2, icon: "fa-edit", color: "#f59e0b" },
                            { key: "delete", label: "Delete", idx: 3, icon: "fa-trash", color: "#ef4444" },
                            { key: "export", label: "Export", idx: 4, icon: "fa-download", color: "#6366f1" },
                            { key: "approve", label: "Approve", idx: 5, icon: "fa-check-circle", color: "#10b981" },
                            { key: "statusupdate", label: "Status", idx: 6, icon: "fa-sync", color: "#8b5cf6" },
                          ].filter(p => rs[p.key] === 1).map(p => {
                            const checked = perms[p.idx] === "1"
                            return (
                              <label key={p.idx} style={{
                                cursor: "pointer", fontSize: "11px", display: "flex", alignItems: "center", gap: "3px",
                                padding: "3px 8px", borderRadius: "4px", border: `1px solid ${checked ? p.color+"40" : "#e2e8f0"}`,
                                background: checked ? p.color+"10" : "transparent", transition: "all 0.15s",
                                color: checked ? p.color : "#94a3b8", fontWeight: checked ? "600" : "400"
                              }}>
                                <input type="checkbox" checked={checked}
                                  onChange={(e) => handleCheckboxChange(e, rs.id, p.idx)}
                                  style={{ width: "13px", height: "13px", accentColor: p.color }} />
                                <i className={`fas ${p.icon}`} style={{ fontSize: "9px" }}></i>
                                {p.label}
                              </label>
                            )
                          })}
                        </div>
                      </Col>
                    </Row>
                  )
                })}
              </div>
            ))}
          </div>

          <div className="mb-3 row mt-4">
            <div className="col-6">{submitted && <UncontrolledAlert color="success"><strong>Role Added Successfully!</strong></UncontrolledAlert>}</div>
            <div className="col-6 text-end">
              <button className="btn btn-primary w-md waves-effect waves-light" type="submit" disabled={loading || modulesLoading || !roleid}>
                {loading ? <><Spinner animation="border" size="sm" /><span style={{ paddingLeft: "10px" }}>Saving...</span></> : <span><i className="fas fa-save"></i> Save Role</span>}
              </button>
            </div>
          </div>
        </AvForm>
      </div>
    </div>
  )
}

export default AddRole
